/**
 * useSafeZoneGuides.ts
 * Hook pour utiliser SafeZoneGuideService dans les composants React
 */
import { useEffect, useState, useCallback } from 'react';
import { useAppStore } from '../services/StateService';
import { SafeZoneGuideService } from '../services/SafeZoneGuideService';
import { useAppCoordinatorOptional } from './useAppCoordinator';
import Konva from 'konva';

/**
 * Hook pour gérer les guides de zone de sécurité
 */
export function useSafeZoneGuides() {
  const [guideService, setGuideService] = useState<SafeZoneGuideService | null>(null);
  const safeZoneVisible = useAppStore(state => state.project.safeZoneVisible);
  const bleedVisible = useAppStore(state => state.project.bleedVisible);
  const appCoordinator = useAppCoordinatorOptional();

  useEffect(() => {
    if (!appCoordinator) return;

    const canvasManager = appCoordinator.getCanvasManager();
    if (!canvasManager) return;

    // Récupérer le stage Konva
    interface CanvasManagerWithStage {
      stage?: Konva.Stage;
    }
    const stage = (canvasManager as CanvasManagerWithStage).stage;
    if (!stage) return;

    // Créer le service de guides
    const service = new SafeZoneGuideService(stage);
    setGuideService(service);

    return () => {
      // Nettoyer si nécessaire
      service.setVisible(false);
    };
  }, [appCoordinator]);

  /**
   * Affiche ou masque les guides
   */
  const setVisible = useCallback(
    (visible: boolean) => {
      if (guideService) {
        guideService.setVisible(visible);
      }
    },
    [guideService]
  );

  /**
   * Met à jour les guides
   */
  const updateGuides = useCallback(() => {
    if (guideService) {
      guideService.updateGuides();
    }
  }, [guideService]);

  // Mettre à jour la visibilité selon l'état du store
  useEffect(() => {
    if (guideService) {
      const shouldShow = safeZoneVisible || bleedVisible;
      setVisible(shouldShow);
    }
  }, [guideService, safeZoneVisible, bleedVisible, setVisible]);

  return {
    guideService,
    setVisible,
    updateGuides,
    isVisible: safeZoneVisible || bleedVisible,
  };
}
